import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { flow } from 'lodash';

import {
  VideoRecorder,
  RecorderState,
  Thumbnail,
  RecordingMetadata,
} from '@vimily/bonjoro-ui-components-v2/VideoRecorder';

import {
  getRecorderState,
  getBlob,
  getThumbnailBlob,
} from '~/App/Components/Record/Stores/Recorder/Selectors';
import * as Actions from '~/App/Components/Record/Stores/Recorder/Actions';

import { assetUrl } from '~/helpers';
import {
  REPLY_BY_VIDEO_STARTED,
  REPLY_BY_VIDEO_ENDED,
} from '~/generated/AnalyticsTypes';
import analytics from '~/utils/Analytics';
import { Reply, ReplyMediaStatus } from '~/types/models/reply';
import RecorderStatus from './RecorderStatus';
import type { UploadUrlResponse } from '~/types/api';
import type { PopupRecorderProps } from './PopupRecorder';
import { useUploadVideoToS3 } from '~/App/Api/S3Upload';
import { cx } from '@emotion/css';

export type RecorderProps = Pick<
  PopupRecorderProps,
  'createReplyForVideoReply' | 'onVideoReplySuccess'
> & {
  onCloseRecorder: () => void;
};

const SendButton = styled.button`
  background: none;
  border: none;
  outline: none;
  margin: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  padding: 10px;
  z-index: 99;

  &:hover,
  &:focus {
    opacity: 0.8;
  }
`;

const ClickableIcon = styled.img`
  cursor: pointer;
`;

function Recorder({
  createReplyForVideoReply,
  onVideoReplySuccess,
  onCloseRecorder,
}: RecorderProps) {
  const dispatch = useDispatch();

  const videoRef = useRef<HTMLVideoElement | undefined>();

  const [isVideoSent, setIsVideoSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadFailed, setIsUploadFailed] = useState(false);

  const videoBlob = useSelector(flow(getRecorderState, getBlob));

  function handleRecordingComplete(
    data: Blob,
    thumbnails: Thumbnail[],
    metadata?: RecordingMetadata
  ) {
    dispatch(Actions.setBlob(data));
    dispatch(Actions.setThumbnails(thumbnails));
    metadata?.duration && dispatch(Actions.setDuration(metadata.duration));
  }

  const [recorderState, setRecorderState] = useState<RecorderState>(
    RecorderState.Loading
  );

  const thumbnailBlobPromise = useSelector(
    flow(getRecorderState, useCallback(getThumbnailBlob(), []))
  );

  const isComplete = useMemo(
    () => recorderState === RecorderState.Complete,
    [recorderState]
  );

  const isRecording = useMemo(
    () => recorderState === RecorderState.Recording,
    [recorderState]
  );

  const uploadVideoToS3 = useUploadVideoToS3();

  useEffect(() => {
    dispatch(Actions.setThumbnail(0));
  }, [isComplete]);

  useEffect(() => {
    if (!isComplete && !isRecording) return;
    const event = isComplete ? REPLY_BY_VIDEO_ENDED : REPLY_BY_VIDEO_STARTED;
    void analytics.track(event, {
      context: 'landing_page',
      url: window.location.href,
    });
  }, [isComplete, isRecording, analytics]);

  async function handleSendButtonClick() {
    setIsLoading(true);
    const reply = await createReplyForVideoReply();

    if (!reply) return;

    void processVideoReply(reply as Reply);
  }

  async function processVideoReply(reply: Reply) {
    if (!videoBlob || !videoRef.current) return;

    try {
      const uploadUrlResponse = await axios.get<UploadUrlResponse>(
        `/api/v2/replies/${reply.id}/media-upload-url`
      );
      const thumbnailBlob = await thumbnailBlobPromise;

      const { url } = uploadUrlResponse.data;

      await uploadVideoToS3(url, videoBlob);

      const formData = new FormData();
      if (thumbnailBlob) formData.append('thumbnail', thumbnailBlob);

      await axios.post(`/api/v2/replies/${reply.id}/media`, formData);
      reply.media = {
        video_url: videoRef.current.src,
        status: ReplyMediaStatus.COMPLETE,
      };
      onVideoReplySuccess(reply);
      setIsLoading(false);
      setIsVideoSent(true);
    } catch (e) {
      setIsLoading(false);
      setIsUploadFailed(true);
    }
  }

  return (
    <div className='relative w-full h-full'>
      <VideoRecorder
        videoRef={videoRef}
        onRecorderStateChange={setRecorderState}
        onRecordingComplete={handleRecordingComplete}
        debug={process.env.APP_DEBUG === 'true'}
        height='100%'
        width='100%'
        className='min-h-0 min-w-0'
        countdownTimerClassName='!v2-ml-4'
      />
      {isComplete && !isVideoSent && !isLoading && !isUploadFailed && (
        // eslint-disable-next-line
        <SendButton onClick={handleSendButtonClick}>
          <ClickableIcon
            src={assetUrl('assets/images/record/ic_send_video.svg')}
          />
        </SendButton>
      )}
      {!isLoading && !isRecording && (
        <div
          role='button'
          className={cx(
            'absolute bg-black/20 rounded-full top-5 !h-auto !w-auto p-[5px] z-99',
            {
              'left-5': recorderState === RecorderState.Complete,
              'right-5': recorderState !== RecorderState.Complete,
            }
          )}
          onClick={onCloseRecorder}
        >
          <img
            className='block h-4 w-4 m-0'
            src={assetUrl('assets/images/icons/ic_close.svg')}
          />
        </div>
      )}
      <RecorderStatus
        isFailed={isUploadFailed}
        isLoading={isLoading}
        isComplete={isComplete && isVideoSent}
      />
    </div>
  );
}

export default Recorder;
